import {
  createContext,
  Dispatch,
  memo,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import ymaps from 'yandex-maps';

import { CdekOffice } from '@shared/types/cdek';
import { Button } from '@shared/ui/Button';
import { CloseIcon } from '@shared/ui/CloseIcon';
import { addPadding, removePadding } from '@shared/utils/scrollHelpers/scrollHelpers';

import { Map } from './ui/Map/Map';
import { PointInfoContent } from './ui/PointInfoContent/PointInfoContent';
import { PointsContent } from './ui/PointsContent/PointsContent';
import { Search } from './ui/Search/Search';
import cls from './CdekWidget.module.scss';

type CdekWidgetProps = {
  open: boolean;
  onClose: () => void;
  onChangeSelectedPoint: (point?: CdekOffice) => void;
  setErrorDeliveryPoint: Dispatch<SetStateAction<string>>;
  selectedPoint?: CdekOffice;
};

type CdekWidgetContext =
  | (Omit<CdekWidgetProps, 'onClose' | 'setErrorDeliveryPoint'> & {
      mapRef: MutableRefObject<ymaps.Map | undefined>;
      points: CdekOffice[];
      selectPoint: (point: CdekOffice) => () => void;
      showPointInfo: (point: CdekOffice) => () => void;
      setPoints: Dispatch<SetStateAction<CdekOffice[]>>;
      closeModal: () => void;
    })
  | null;

const CdekWidgetContext = createContext<CdekWidgetContext>(null);

export const CdekWidget = memo<CdekWidgetProps>(
  ({ open, onClose, selectedPoint, setErrorDeliveryPoint, onChangeSelectedPoint }) => {
    const mapRef = useRef<ymaps.Map>();

    const [points, setPoints] = useState<CdekOffice[]>([]);

    const resetSelectedPoint = useCallback(() => {
      onChangeSelectedPoint(undefined);
    }, [onChangeSelectedPoint]);

    const onCloseWidget = useCallback(() => {
      setErrorDeliveryPoint('');
      onClose();
    }, [setErrorDeliveryPoint, onClose]);

    const selectPoint = useCallback(
      (point: CdekOffice) => () => {
        onChangeSelectedPoint(point);
        onCloseWidget();
      },
      [onChangeSelectedPoint, onCloseWidget]
    );

    const showPointInfo = useCallback(
      (point: CdekOffice) => () => {
        onChangeSelectedPoint(point);
      },
      [onChangeSelectedPoint]
    );

    useEffect(() => {
      if (open) {
        addPadding();
      } else {
        removePadding();
      }
    }, [open]);

    if (!open) {
      return null;
    }

    return (
      <CdekWidgetContext.Provider
        value={{
          open,
          mapRef,
          points,
          selectPoint,
          showPointInfo,
          selectedPoint,
          onChangeSelectedPoint,
          setPoints,
          closeModal: onClose
        }}
      >
        <div className={cls.wrapper}>
          <Map />
          <div className={cls.deliveryWrapper}>
            <CloseIcon
              className={cls.closeIcon}
              onClick={selectedPoint ? resetSelectedPoint : onClose}
            />
            <div className={cls.titleWrapper}>
              <div className={cls.title}>{selectedPoint ? 'Пункт выдачи' : 'Выберите пункт выдачи'}</div>
              {selectedPoint ? (
                <Button
                  fullWidth
                  onClick={onCloseWidget}
                >
                  Заберу отсюда
                </Button>
              ) : (
                <Search />
              )}
            </div>
            {selectedPoint ? <PointInfoContent /> : <PointsContent />}
          </div>
        </div>
      </CdekWidgetContext.Provider>
    );
  }
);

export const useCdekWidget = () => useContext(CdekWidgetContext);
